
import { useState, useEffect, useMemo } from 'react';
import { fetchPublicDisbursements, type DisbursementRecord } from '../../../api/dripPlan';

const districts = ['全部区域', '渝中区', '江北区', '南岸区', '九龙坡区', '沙坪坝区', '渝北区', '巴南区', '北碚区'];

type DisplayRecord = {
  id: number;
  date: string;
  location: string;
  recipient: string;
  bottles: number;
  receiver: string;
  proof: string;
  title: string;
  amount: number;
};

export default function DistributionRecords() {
  const [records, setRecords] = useState<DisplayRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDistrict, setSelectedDistrict] = useState('全部区域');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProof, setSelectedProof] = useState<string | null>(null);
  const itemsPerPage = 5;

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      setLoading(true);
      fetchPublicDisbursements()
        .then((list: DisbursementRecord[]) => {
          if (cancelled) return;
          setRecords(
            list.map((r) => ({
              id: r.id,
              date: r.date || '',
              location: r.region || '—',
              recipient: r.beneficiary || r.title || '—',
              bottles: r.quantity != null ? Number(r.quantity) : Math.floor(Number(r.amount) / 19.9) * 12,
              receiver: r.remark || r.beneficiary || '—',
              proof: '',
              title: r.title,
              amount: Number(r.amount) || 0,
            }))
          );
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  const filteredRecords = useMemo(() => {
    return records.filter((record) => {
      const districtMatch =
        selectedDistrict === '全部区域' ||
        record.location.includes(selectedDistrict.replace('区', ''));
      const startDateMatch = !startDate || record.date >= startDate;
      const endDateMatch = !endDate || record.date <= endDate;
      return districtMatch && startDateMatch && endDateMatch;
    });
  }, [records, selectedDistrict, startDate, endDate]);

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, startIndex + itemsPerPage);

  const handleReset = () => {
    setSelectedDistrict('全部区域');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6 md:p-8">
      <h2 className="text-xl md:text-2xl font-bold text-[#1E1E1E] mb-4 md:mb-6">发放记录查询</h2>

      <div className="bg-[#F9F9F9] rounded-lg p-4 md:p-6 mb-4 md:mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 md:gap-4">
          <div>
            <label className="block text-xs md:text-sm font-medium text-[#1E1E1E] mb-2">区域筛选</label>
            <select
              value={selectedDistrict}
              onChange={(e) => {
                setSelectedDistrict(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 md:px-4 py-2 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] bg-white text-xs md:text-sm"
            >
              {districts.map((district) => (
                <option key={district} value={district}>
                  {district}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs md:text-sm font-medium text-[#1E1E1E] mb-2">开始日期</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 md:px-4 py-2 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] text-xs md:text-sm"
            />
          </div>

          <div>
            <label className="block text-xs md:text-sm font-medium text-[#1E1E1E] mb-2">结束日期</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 md:px-4 py-2 border border-[#E0E0E0] rounded-lg focus:outline-none focus:border-[#2A7A62] text-xs md:text-sm"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={handleReset}
              className="w-full px-3 md:px-4 py-2 bg-white border border-[#2A7A62] text-[#2A7A62] rounded-lg hover:bg-[#2A7A62]/5 transition-all text-xs md:text-sm font-medium whitespace-nowrap"
            >
              重置筛选
            </button>
          </div>
        </div>

        <div className="mt-3 md:mt-4 text-xs md:text-sm text-[#6C6C6C]">
          共找到 <span className="font-bold text-[#2A7A62]">{filteredRecords.length}</span> 条记录
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-[#6C6C6C]">加载中...</div>
      ) : filteredRecords.length === 0 ? (
        <div className="py-16 text-center text-[#6C6C6C]">暂无发放记录</div>
      ) : (
        <>
      <div className="block md:hidden space-y-3">
        {paginatedRecords.map((record) => (
          <div key={record.id} className="bg-[#F9F9F9] rounded-lg p-4">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <span className="text-[#6C6C6C]">日期</span>
                <p className="font-medium text-[#1E1E1E] mt-1">{record.date}</p>
              </div>
              <div>
                <span className="text-[#6C6C6C]">地点</span>
                <p className="font-medium text-[#1E1E1E] mt-1">{record.location}</p>
              </div>
              <div className="col-span-2">
                <span className="text-[#6C6C6C]">发放项目</span>
                <p className="font-medium text-[#1E1E1E] mt-1">{record.title}</p>
              </div>
              <div>
                <span className="text-[#6C6C6C]">受助单位</span>
                <p className="font-medium text-[#1E1E1E] mt-1">{record.recipient}</p>
              </div>
              <div>
                <span className="text-[#6C6C6C]">发放瓶数</span>
                <p className="font-bold text-[#2A7A62] mt-1">{record.bottles}瓶</p>
              </div>
              <div>
                <span className="text-[#6C6C6C]">金额</span>
                <p className="font-bold text-[#2A7A62] mt-1">¥{record.amount}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[#F9F9F9]">
              <th className="px-4 py-3 text-left text-sm font-bold text-[#1E1E1E]">日期</th>
              <th className="px-4 py-3 text-left text-sm font-bold text-[#1E1E1E]">地点</th>
              <th className="px-4 py-3 text-left text-sm font-bold text-[#1E1E1E]">发放项目</th>
              <th className="px-4 py-3 text-left text-sm font-bold text-[#1E1E1E]">受助单位</th>
              <th className="px-4 py-3 text-left text-sm font-bold text-[#1E1E1E]">发放瓶数</th>
              <th className="px-4 py-3 text-left text-sm font-bold text-[#1E1E1E]">金额</th>
            </tr>
          </thead>
          <tbody>
            {paginatedRecords.map((record) => (
              <tr key={record.id} className="border-b border-[#E0E0E0] hover:bg-[#F9F9F9] transition-colors">
                <td className="px-4 py-4 text-sm text-[#1E1E1E]">{record.date}</td>
                <td className="px-4 py-4 text-sm text-[#1E1E1E]">{record.location}</td>
                <td className="px-4 py-4 text-sm text-[#1E1E1E]">{record.title}</td>
                <td className="px-4 py-4 text-sm text-[#1E1E1E]">{record.recipient}</td>
                <td className="px-4 py-4 text-sm font-bold text-[#2A7A62]">{record.bottles}瓶</td>
                <td className="px-4 py-4 text-sm font-bold text-[#2A7A62]">¥{record.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4 md:mt-6">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 md:px-4 py-2 border border-[#E0E0E0] rounded-lg hover:bg-[#F9F9F9] disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap text-sm md:text-base"
          >
            <i className="ri-arrow-left-s-line"></i>
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 md:px-4 py-2 rounded-lg transition-all whitespace-nowrap text-sm md:text-base ${
                currentPage === page
                  ? 'bg-[#2A7A62] text-white'
                  : 'border border-[#E0E0E0] hover:bg-[#F9F9F9]'
              }`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 md:px-4 py-2 border border-[#E0E0E0] rounded-lg hover:bg-[#F9F9F9] disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap text-sm md:text-base"
          >
            <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>
      )}
        </>
      )}

      {selectedProof && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedProof(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-4" onClick={(e) => e.stopPropagation()}>
            <img src={selectedProof} alt="发放凭证" className="w-full rounded-lg" />
          </div>
        </div>
      )}
    </div>
  );
}
